/**
 * Resolves the tri-state email-verification signal from the Auth0 OIDC claim
 * and exposes a `refresh()` that forces a live re-read on each platform
 * (ENG-3797, spec-fragment.md §2/§3 Path 1).
 *
 * WHY the verify path NEVER forces a re-login (ENG-3797 auth fix): the earlier
 * build resolved the browser signal from the ID/access token via
 * getAccessTokenSilently({cacheMode:'off'}) + getIdTokenClaims(). For Auth0
 * custom-DB (email/password) users a silent refresh-token grant does NOT re-run
 * the login script, so the refreshed token kept email_verified:false even after
 * the user clicked the verify link — the gate never saw "verified" and then
 * escalated to loginWithRedirect({prompt:'login'}), bouncing a pending user to
 * the Auth0 login screen (the confirmed live-test bug). The fix mirrors the
 * Electron path on both surfaces: poll Auth0's live `/userinfo` endpoint, which
 * reflects Auth0's CURRENT user record and flips email_verified to true the
 * moment the user verifies — no token re-mint, no re-login. The forced-re-auth
 * escalation is removed entirely (spec §3 Path 1: auto-dismiss into the app).
 */

import { useCallback, useMemo, useState } from 'react';
import { useAuth0 as useAuth0Sdk } from '@auth0/auth0-react';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { authStore } from '#~/auth/store.js';
import { config } from '#~/config/index.js';
import {
  getSurface,
  isSsoSubject,
  readClaims,
  resolveVerificationSignal,
  type VerificationClaims,
  type VerificationSignal,
  type VerificationSurface
} from './logic.js';
import { isLatchedVerified, latchVerified } from './verified-latch.js';

export interface VerificationSignalState {
  /** Safely-extracted Auth0 claims (email is for display/change-email only). */
  claims: VerificationClaims;
  /** Tri-state signal driving the gate (spec §4). */
  signal: VerificationSignal;
  /** Analytics surface dimension. */
  surface: VerificationSurface;
  /** True when the subject is an SSO/IdP user (spec §5 bypass). */
  sso: boolean;
  /**
   * Force a live re-read of the verification state. BOTH surfaces hit Auth0's
   * live `/userinfo` endpoint with the current bearer access token, because that
   * reflects Auth0's current user record and flips `email_verified` to true the
   * instant the user verifies — unlike a silently-refreshed token, which never
   * re-runs the custom-DB login script and stays stale. Browser issues the GET
   * directly; Electron goes through `ElectronAPI.getUserInfo()` (same endpoint,
   * main-process side). Rejects on a genuine transport failure so the caller can
   * surface a poll-failure error; never triggers any redirect or re-login.
   */
  refresh: () => Promise<void>;
}

/**
 * Hook form. Reads `user` from the platform-aware `useAuth0` wrapper (Electron
 * → `userInfo` from `/userinfo`; browser → Auth0 SDK user). The browser refresh
 * takes `getAccessTokenSilently` from the auth0-react SDK hook directly (not the
 * platform wrapper, whose variant is the no-arg Electron stub) to obtain the
 * bearer token it uses to authenticate the live `/userinfo` lookup.
 */
export const useVerificationSignal = (): VerificationSignalState => {
  const { user } = useAuth0();
  const { getAccessTokenSilently } = useAuth0Sdk();

  // Claims re-read live during an in-session refresh (ENG-3797 Failure 3). The
  // auth0-react `user` object that drives `signal` is only refreshed on a full
  // page reload, so the auto-poll (and "Retry") after verifying would never flip
  // the gate to 'allow' within the same page session. We read the live
  // `/userinfo` payload on each refresh and feed it in here, so the gate
  // transitions without a reload. `undefined` means "no live read yet" → fall
  // back to the SDK `user`.
  const [liveClaims, setLiveClaims] = useState<VerificationClaims | undefined>(undefined);

  const baseClaims = useMemo(() => readClaims(user), [user]);
  // Live claims (from an in-session refresh) win over the SDK `user` — BUT only
  // when they describe the EXACT SAME identity (same sub AND same email). If the
  // SDK `user` switches to a different account, OR the same user changes their
  // email (the account-settings re-verify flow), OR the base identity is unknown
  // (user object not yet/no longer loaded), the live read is discarded and we
  // fall back to `baseClaims`. This prevents a stale verified `liveClaims` from
  // shadowing a new/unknown identity (codex round-1 finding 1, round-2 finding,
  // round-3 finding): the verified state for a genuinely-latched identity is held
  // by the cross-remount latch below (keyed by sub+email), NOT by letting a live
  // claim win against an unknown base — so an unknown base correctly resolves
  // fail-closed to 'unknown' rather than inheriting the prior verified claim.
  const claims =
    liveClaims !== undefined &&
    liveClaims.sub === baseClaims.sub &&
    liveClaims.email === baseClaims.email
      ? liveClaims
      : baseClaims;

  // Resolve the signal, then apply the SESSION VERIFIED LATCH as a final
  // override (ENG-3797 flicker fix). The latch lives OUTSIDE component state
  // (verified-latch.ts), so once any live /userinfo read has observed this
  // identity verified, a gate REMOUNT — which resets `liveClaims` to undefined
  // and makes `claims` fall back to the stale custom-DB SDK `user`
  // (email_verified:false) — can no longer revert the signal to 'unverified'
  // and flash the pending screen. A genuinely verified email never un-verifies
  // mid-session, so this is monotonic and safe. The latch key is `sub` + `email`
  // (codex round-2 finding): it can never grant a different user, NOR a
  // same-`sub` identity whose email was changed (which the backend re-marks
  // unverified). The identity for the lookup is taken from the SAME claims the
  // signal is derived from, so a subject/email switch reads the new identity (an
  // unlatched one) rather than the prior verified key. Fail-closed is preserved:
  // only an explicit verified:true ever latches (see commitClaims), never an
  // unknown.
  const resolved = resolveVerificationSignal(claims);
  const signal: VerificationSignal =
    resolved === 'verified' ||
    isLatchedVerified({
      sub: claims.sub ?? baseClaims.sub,
      email: claims.email ?? baseClaims.email
    })
      ? 'verified'
      : resolved;
  const surface = getSurface();
  const sso = isSsoSubject(claims.sub);

  // Commit a freshly-read live claim, keeping `verified` STICKY. Auth0 never
  // reverts a verified record to unverified, so once any poll has observed
  // email_verified:true a later out-of-order /userinfo response (overlapping
  // polls, a slow request that resolves after a faster one) must NOT clobber it
  // back to unverified and bounce the user out of the app (codex review b2).
  // On an explicit verified:true we also write the cross-remount latch above, so
  // the verified state survives an unmount/remount (the confirmed flicker), not
  // just out-of-order responses within one mount.
  //
  // The stickiness is IDENTITY-SCOPED — sub AND email (codex round-1 finding 1 +
  // round-2 finding): only keep a prior verified claim when the incoming claim is
  // for the SAME sub AND the SAME email. A different subject (account switched in
  // this mounted gate) OR a changed email (the account-settings re-verify flow,
  // which the backend re-marks unverified) always replaces it, so a stale
  // verified claim can never shadow a new unverified identity.
  const commitClaims = useCallback((next: VerificationClaims): void => {
    if (next.emailVerified === true) {
      latchVerified({ sub: next.sub, email: next.email });
    }
    // Keep the prior verified claim only for the SAME identity (sub AND email).
    // A different subject OR a changed email (the account-settings re-verify
    // flow) always replaces it, so a stale verified claim cannot shadow a new
    // unverified identity (codex round-1 finding 1 + round-2 finding).
    setLiveClaims((prev) =>
      prev?.emailVerified === true &&
      prev.sub === next.sub &&
      prev.email === next.email &&
      next.emailVerified !== true
        ? prev
        : next
    );
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (isElectronApp()) {
      // Live /userinfo re-fetch reflects current Auth0 verification state even
      // for DB users (spec §3 Path 1; the registry value never re-syncs them).
      //
      // force=true is REQUIRED here (ENG-3797 Electron auth fix): without it the
      // main process returns its persisted `userInfo`, which for a custom-DB
      // signup is frozen at email_verified:false (a silent token refresh never
      // re-runs the login script). The gate would then never observe
      // verification and never auto-dismiss in Electron — the browser path works
      // only because it issues its own live /userinfo GET below. Passing true
      // makes getUserInfo bypass the cache and hit Auth0 /userinfo live.
      const { data } = await ElectronAPI.getUserInfo(true);
      // Null data is a failed live read (main swallows non-NetworkError into
      // null). Treating it as success reset the poll failure counter and left
      // the gate pending forever (ENG-3797 slot F / ERR-AC93).
      if (data === null || data === undefined) {
        throw new Error('userinfo empty');
      }
      authStore.setState({ userInfo: data });
      // The authStore write already updates the wrapper `user`; also feed the
      // claims into liveClaims for parity with the browser path (Failure 3).
      commitClaims(readClaims(data));
      return;
    }

    // Browser: read Auth0's LIVE /userinfo, the same source the Electron path
    // uses. CRITICAL (ENG-3797): we must NOT resolve verification from a
    // silently-refreshed token — a custom-DB refresh-grant never re-runs the
    // login script, so the token stays email_verified:false forever and the gate
    // would never auto-dismiss (and the old build then forced a re-login).
    // /userinfo reflects Auth0's current user record and flips to true the moment
    // the user verifies. A cached access token is fine here: it only AUTHENTICATES
    // the lookup; the verification state comes from /userinfo, not the token body.
    const accessToken = await getAccessTokenSilently();
    // The SDK resolves undefined past the session_expiry ceiling; login_required keeps the gate pending.
    // Plain Error, not GenericError: gate tests mock @auth0/auth0-react down to useAuth0.
    if (!accessToken) {
      throw Object.assign(new Error('No access token is available'), { error: 'login_required' });
    }

    // A failed claim parse must NOT turn a successful 200 into a poll failure —
    // a malformed payload degrades to "unknown" via readClaims (fail-closed),
    // and the next poll re-reads. Only a non-OK response / transport error
    // rejects, so the gate's poll loop surfaces a genuine connection failure.
    const response = await fetch(`https://${config.oauth.url}/userinfo`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (!response.ok) {
      // Reject so the poll loop counts this as a connection failure (it will
      // only reach the error screen after a sustained run, per Failure 1).
      throw new Error(`userinfo ${response.status}`);
    }
    const data: unknown = await response.json();
    commitClaims(readClaims(data));
  }, [getAccessTokenSilently, commitClaims]);

  return { claims, signal, surface, sso, refresh };
};
